import { defineStore } from 'pinia';
import { ref } from 'vue';
import { useApi } from '@lib/api.ts';
import { useUser } from '@/store/user';

// Which suppliers are attached to an article or a component, for the "in use" filters.
//
// Not a global store on purpose: these flags flip on every BOM edit, so holding them live would push
// a reload to every open tab throughout a component import. Nothing reads them until a page with a
// supplier filter renders, so they are fetched when such a page mounts and left alone otherwise.
export const useSupplierUsage = defineStore('supplier-usage', () => {
  // Resolved here rather than per call: `useApi` is an `inject`, so it only answers while a
  // component is setting up.
  const api = useApi();

  const onArticles = ref<Set<number>>(new Set());
  const onComponents = ref<Set<number>>(new Set());
  const loadedBrandId = ref<number | null>(null);
  let inFlight: Promise<void> | null = null;

  async function load(): Promise<void> {
    const brandId = useUser().brandId;

    // Another brand's answer is worse than none while the new one is on its way.
    if (loadedBrandId.value !== null && loadedBrandId.value !== brandId) {
      onArticles.value = new Set();
      onComponents.value = new Set();
      loadedBrandId.value = null;
    }

    if (inFlight) return inFlight;

    inFlight = (async () => {
      const rows = await api.brandSupplier_Usage(brandId);
      onArticles.value = new Set(rows.filter((r) => r.onArticles).map((r) => r.id));
      onComponents.value = new Set(rows.filter((r) => r.onComponents).map((r) => r.id));
      loadedBrandId.value = brandId;
    })();

    try {
      await inFlight;
    } finally {
      inFlight = null;
    }
  }

  return { onArticles, onComponents, load };
});
